const RECORDING_MIME_TYPES = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4", "audio/webm"];
const RECORDING_TIMESLICE_MS = 1000;
// Speech in mono at 32 kbps: about 22 MB for the 90-minute maximum. The
// browser's default rate is several times that, which a voice doesn't need
// and the results player would have to download and decode.
const RECORDING_BITS_PER_SECOND = 32_000;
/** How long stop() waits for the recorder's last slice before keeping what it has. */
export const RECORDING_STOP_TIMEOUT_MS = 5000;
const DEFAULT_UPLOAD_TYPE = "audio/webm";
function pickMimeType() {
  if (typeof MediaRecorder.isTypeSupported !== "function") return "";
  return RECORDING_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) || "";
}


/**
 * Starts recording the call from the capture's mixed stream. The caller keeps
 * ownership of the stream; stopping the recorder never stops its tracks.
 *
 * @param {MediaStream} mixedStream the mic and call audio as one track
 * @returns {{ stop: () => Promise<Blob|null>, mimeType: string }|null} null when
 *   this browser cannot record or the recorder fails to start: a recording
 *   never blocks the session. stop() resolves with the whole recording (null if
 *   nothing was captured) and is idempotent. A recorder that never reports it
 *   stopped can't hold up the end of the call: after RECORDING_STOP_TIMEOUT_MS
 *   stop() resolves with the slices it has.
 */
export function startAudioRecording(mixedStream) {
  if (typeof MediaRecorder === "undefined" || !mixedStream) return null;

  let recorder;
  let preferredType = "";
  const chunks = [];
  try {
    preferredType = pickMimeType();
    recorder = new MediaRecorder(mixedStream, {
      ...(preferredType ? { mimeType: preferredType } : {}),
      audioBitsPerSecond: RECORDING_BITS_PER_SECOND,
    });
    recorder.addEventListener("dataavailable", (event) => {
      if (event.data?.size > 0) chunks.push(event.data);
    });
    recorder.start(RECORDING_TIMESLICE_MS);
  } catch {
    return null;
  }
  const mimeType = recorder.mimeType || preferredType;

  let stopped = null;
  function stop() {
    if (stopped) return stopped;
    stopped = new Promise((resolve) => {
      let timer = null;
      const finish = () => {
        clearTimeout(timer);
        resolve(chunks.length > 0 ? new Blob(chunks, { type: mimeType || DEFAULT_UPLOAD_TYPE }) : null);
      };
      if (recorder.state === "inactive") {
        finish();
        return;
      }
      recorder.addEventListener("stop", finish, { once: true });
      timer = setTimeout(finish, RECORDING_STOP_TIMEOUT_MS);
      try {
        recorder.stop();
      } catch {
        finish();
      }
    });
    return stopped;
  }

  return { stop, mimeType };
}
